/**
 * Variant Metafield Manager
 * 
 * Centralized variant detection and event system for variant-based metafield updates.
 * This eliminates code duplication across multiple snippet files.
 */

(function() {
  'use strict';

  // Store the current variant ID
  let currentVariantId = null;
  let firstVariantId = null;
  let urlCheckInterval = null;
  let isReady = false; // Track if manager is ready

  /**
   * Get the current variant ID from hidden input or URL
   * @param {string} fallbackId - Fallback variant ID if none found
   * @returns {string} The current variant ID
   */
  function getCurrentVariantId(fallbackId) {
    // Try to get from hidden input
    const variantInput = document.querySelector("input[type='hidden'].product-variant-id");
    if (variantInput && variantInput.value) {
      return variantInput.value;
    }
    
    // Try to get from URL
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      return variantId;
    }
    
    // Use fallback or stored first variant ID
    return fallbackId || firstVariantId || null;
  }

  /**
   * Dispatch variant change event
   * @param {string} variantId - The new variant ID
   */
  function dispatchVariantChange(variantId) {
    if (variantId === currentVariantId) {
      return; // No change
    }
    
    currentVariantId = variantId;
    
    // Dispatch custom event
    const event = new CustomEvent('variant:change', {
      detail: { variantId: variantId },
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  /**
   * Initialize variant detection and event system
   */
  function init() {
    // Try to get first variant ID from the page
    const variantInput = document.querySelector("input[type='hidden'].product-variant-id");
    if (variantInput) {
      firstVariantId = variantInput.value || variantInput.getAttribute('value');
    }

    // Initialize with current variant
    const initialVariantId = getCurrentVariantId();
    if (initialVariantId) {
      dispatchVariantChange(initialVariantId);
    }

    // Listen for changes on the hidden input
    if (variantInput) {
      variantInput.addEventListener('change', () => {
        const newVariantId = variantInput.value || firstVariantId;
        if (newVariantId) {
          dispatchVariantChange(newVariantId);
        }
      });
    }

    // Monitor URL changes (for cases where variant is changed via URL)
    let lastUrl = window.location.href;
    urlCheckInterval = setInterval(() => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        const newVariantId = getCurrentVariantId();
        if (newVariantId) {
          dispatchVariantChange(newVariantId);
        }
      }
    }, 100);

    // Listen to popstate events (browser back/forward)
    window.addEventListener('popstate', () => {
      const newVariantId = getCurrentVariantId();
      if (newVariantId) {
        dispatchVariantChange(newVariantId);
      }
    });
  }

  /**
   * Cleanup function
   */
  function cleanup() {
    if (urlCheckInterval) {
      clearInterval(urlCheckInterval);
      urlCheckInterval = null;
    }
  }

  // Expose API to window object
  window.VariantMetafieldManager = {
    /**
     * Get current variant ID
     * @param {string} fallbackId - Optional fallback variant ID
     * @returns {string} Current variant ID
     */
    getCurrentVariantId: getCurrentVariantId,

    /**
     * Subscribe to variant changes
     * @param {Function} callback - Callback function that receives { variantId: string }
     * @returns {Function} Unsubscribe function
     */
    onVariantChange: function(callback) {
      const handler = (event) => {
        callback(event.detail.variantId);
      };
      document.addEventListener('variant:change', handler);
      
      // Return unsubscribe function
      return function() {
        document.removeEventListener('variant:change', handler);
      };
    },

    /**
     * Manually trigger variant change (for edge cases)
     * @param {string} variantId - The variant ID to set
     */
    setVariantId: function(variantId) {
      dispatchVariantChange(variantId);
    },

    /**
     * Check if manager is ready
     * @returns {boolean} True if manager is initialized
     */
    isReady: function() {
      return isReady;
    },

    /**
     * Cleanup (useful for testing or page transitions)
     */
    cleanup: cleanup
  };

  // Initialize when DOM is ready
  function startInit() {
    init();
    
    // Mark as ready
    isReady = true;
    
    // Dispatch ready event for snippets that may load before this script
    const readyEvent = new CustomEvent('variant-manager:ready', {
      bubbles: true
    });
    document.dispatchEvent(readyEvent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startInit);
  } else {
    startInit();
  }

  // Cleanup on page unload
  window.addEventListener('beforeunload', cleanup);
})();

